import { ParsedUrlQuery } from 'querystring';

import cx from 'classnames';
import { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { MultiSelect } from '../../../design-system/components/form/multiSelect/MultiSelect';
import { IconFilter } from '../../../design-system/components/icons/Filter';
import { ModalProps } from '../../../design-system/components/modal/Modal';
import { useModal } from '../../../design-system/components/modal/useModal';
import { ItinerariesListAvailableFilters } from '../../../resources/itinerary/domain/types/Itinerary';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './Filters.module.css';
import { FiltersModal } from './FiltersModal';

type FiltersProps = {
    isLoading: boolean;
    filters: ParsedUrlQuery;
    availableFilters: PickDomain<ItinerariesListAvailableFilters, 'filters'>['filters'];
    onFilter: (filters: ParsedUrlQuery) => void;
    onRemoveAllFilters: () => void;
};

export const Filters = ({
    isLoading,
    filters,
    availableFilters,
    onFilter,
    onRemoveAllFilters,
}: FiltersProps) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const filtersModalProps = useModal();
    const [modalSize, setModalSize] = useState<ModalProps['size']>('medium');

    const availableFiltersWithChoices = availableFilters.filter((filter) => {
        return filter.type === 'choices' && filter.choices.length > 0;
    });

    return (
        <>
            <div className={style.filtersWrapper}>
                <div className="mb-28 block md:hidden">
                    <Button
                        Icon={IconFilter}
                        iconPosition="left"
                        onClick={() => {
                            filtersModalProps.open();
                            setModalSize('fullscreen');
                        }}
                        priority="secondary"
                        isFullWidth
                    >
                        {formatMessage({
                            id: 'page.itinerariesList.filters.all',
                        })}
                    </Button>
                </div>
                <div className={style.container}>
                    <div className={cx('hide-below-md', style.selectContainer)}>
                        {availableFiltersWithChoices.slice(0, 3).map((filter) => {
                            const options = filter.choices.map((choice) => {
                                const label =
                                    choice.translation ||
                                    formatMessage({
                                        id: `page.itinerariesList.filters.${choice.name}` as MessageDescriptor['id'],
                                    });

                                return {
                                    value: choice.name,
                                    label,
                                };
                            });

                            const selectedOptions = filter.choices
                                .filter((choice) => {
                                    const filterValue = filters[filter.name];
                                    return Array.isArray(filterValue)
                                        ? filterValue.includes(choice.name)
                                        : filterValue === choice.name;
                                })
                                .map((choice) => {
                                    return {
                                        value: choice.name,
                                        label: choice.translation ?? '',
                                    };
                                });

                            return (
                                <MultiSelect
                                    disabledOptions={isLoading}
                                    key={filter.name}
                                    options={options}
                                    selectedOptions={selectedOptions}
                                    label={formatMessage({
                                        id: `page.itinerariesList.filters.${filter.name}` as MessageDescriptor['id'],
                                    })}
                                    onMenuToggle={(isOpen) => {
                                        if (isOpen) {
                                            trackEvent((ampli, defaultProperties) => {
                                                ampli.listItinerariesFilterClicked({
                                                    ...defaultProperties,
                                                    filter_name: filter.name,
                                                });
                                            });
                                        }
                                    }}
                                    onChange={(selectedOptions) => {
                                        if (isLoading) {
                                            return;
                                        }

                                        onFilter({
                                            ...filters,
                                            [filter.name]: selectedOptions.map((option) => {
                                                return option.value;
                                            }),
                                        });

                                        trackEvent((ampli, defaultProperties) => {
                                            const lastSelectedOption =
                                                selectedOptions[selectedOptions.length - 1]?.value;

                                            ampli.listItinerariesFilterSelected({
                                                ...defaultProperties,
                                                filter_name: filter.name,
                                                filter_value: lastSelectedOption,
                                            });
                                        });
                                    }}
                                />
                            );
                        })}
                        <Button
                            Icon={IconFilter}
                            iconPosition="left"
                            onClick={() => {
                                filtersModalProps.open();
                                setModalSize('medium');
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.listItinerariesFilterClicked({
                                        ...defaultProperties,
                                        filter_name: 'all',
                                    });
                                });
                            }}
                            priority="secondary"
                        >
                            {formatMessage({
                                id: 'page.itinerariesList.filters.all',
                            })}
                        </Button>

                        {Object.values(filters).length > 0 ? (
                            <Button
                                priority="tertiary"
                                onClick={() => {
                                    onRemoveAllFilters();
                                }}
                            >
                                {formatMessage({
                                    id: 'page.itinerariesList.filters.delete-all',
                                })}
                            </Button>
                        ) : null}
                    </div>
                </div>

                <FiltersModal
                    size={modalSize}
                    modalProps={filtersModalProps}
                    filters={filters}
                    availableFilters={availableFilters}
                    onFilter={(filters) => {
                        onFilter(filters);
                    }}
                />
            </div>
        </>
    );
};
